import axios from 'axios';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import LoaderComponent from 'components/LoaderComponent';
import LogoDark from 'LogoDark';
import moment from 'moment';
import React, { useEffect, useState } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Form, Icon, Input, Radio } from "semantic-ui-react";
import { getCovidTestRegistrationData, registerCovidTestkitResult } from 'services/testkitServices';
import styled from "styled-components";
import { InterventionExternalCode } from 'ts-types/api.enums';
import { CovidTestRegistrationData } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { ctValidator } from 'utils/validatorUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledLogo = styled(LogoDark)`
  &.logo-icon {
    transform: scale(0.6);
    margin-top: 0.2rem;
    margin-left: -2.5rem;
    height: 2rem;
  }
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  margin: 0 1.5rem;

  .page-header {
    flex: 0 0 auto;
    border-bottom: 1px solid #2D404A;
    padding: 0.9rem 0;
    font-size: 1.4rem;
    margin: 0 0 2rem;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    .praxmedic-logo-container {
      flex: 1 1 auto;
      width: 2.675em;
      max-width: 9.675rem;
      height: 2.675rem;
    }

    .header-title {
      flex: 0 0 auto;
      line-height: 2.775rem;
      color: #0f1a52;
    }
  }

  .page-footer {
    flex: 0 0 auto;
    border-top: 1px solid #2D404A;
    padding: 0.35rem 1rem 0.5rem;
    font-size: 1.1rem;
    margin: 1.2rem 0 0;
    color: #0f1a52;

    .left {
      float: left;
    }

    .right {
      float: right;
    }
  }

  .result-form .error {
    margin: 0;
    padding: 0;
    background: none;
  }

  @media only screen and (min-width: 320px) and (max-width: 767px) {
    .page-header {
      margin-bottom: 0.3rem;
    }

    .page-footer {
      padding: 0.35rem 0.15rem 0.5rem;
      font-size: 1rem;
    }
  }
`;

const StyledContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: start;
  flex-direction: column;
  max-width: 768px;

  .info-text {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }

  video {
    border: 2px solid;
  }

  .snapshot-container {
    display: flex;
    flex-direction: column;
    align-self: center;
    align-items: center;

    .image-input {
      display: none;
    }

    .snapshot-placeholder {
      display: flex;
      flex-direction: column;
      max-width: 80vh;
      min-width: 30vh;
      min-height: 20vh;
      align-items: center;
      border: 1px solid #e5e5e5;
      padding-top: 1rem;
      border-radius: .28571429rem;

      i.icon {
        color: #e5e5e5;
      }
    }

    .take-snapshot-action {
      display: flex;
      flex-direction: column;
      margin-top: 1rem;
      align-items: center;

      i.icon {
        color: #2185d0;
      }

      .action-label {
        color: #1678c2;
        font-size: 1.1rem;
      }
    }

    .image-box {
      max-width: 90%;
      max-height: 50vh;
      margin: 10px;
      text-align: center;

      img {
        height: inherit;
        max-height: inherit;
        max-width: inherit;
      }
    }
  }

  .radio-box {
    margin: 2rem 0;

    .field-label {
      margin-right: 2rem;
    }

    .field {
      display: inline-block;
      margin-right: 1.5rem;
    }
  }

  .ct-value {
    display: inherit;
    margin-bottom: 2rem;
    align-items: center;

    .field-label {
      margin-right: 1rem;
    }

    .input-field {
      width: 4rem;
      height: 2rem;
    }
  }

  .result {
    margin: 1rem 0 2.5rem;
    font-size: 1.2rem;
    font-weight: 500;
  }

  .warning, .success, .error {
    border-radius: .28571429rem;
    padding: 1rem;
    font-size: 1.2rem;
    margin-bottom: .5rem;
    width: 100%;
  }

  .warning {
    box-shadow: 0 0 0 1px #c9ba9b inset, 0 0 0 0 transparent;
    background-color: #fffaf3;
    color: #573a08;
  }

  .success {
    box-shadow: 0 0 0 1px #a3c293 inset, 0 0 0 0 transparent;
    background-color: #fcfff5;
    color: #2c662d;
  }

  .error {
    box-shadow: 0 0 0 1px #e0b4b4 inset, 0 0 0 0 transparent;
    background-color: #fff6f6;
    color: #9f3a38;
  }

  .info-message {
    margin: 1rem 0;
    font-size: 1.2rem;
  }
`;

const StyledInfoRow = styled.div`
  background-color: ${(props: InfoRowProps) => props.backgroundColor || 'inherited'};
  width: 100%;
  display: flex;
  padding: 0.5rem;
  border-radius: .28571429rem;

  *:first-child {
    width: 150px;
  }

  *:nth-child(2) {
    flex: 1;
  }

  &.target-employee {
    margin-bottom: 1.5rem;
  }
`;

interface InfoRowProps {
  backgroundColor?: string;
}

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;

  .ui.button {
    margin-right: 0.75rem;
  }
`;

interface Props extends MessageSourceProps,
    RouteComponentProps {
}

type PageState = "ENTRY" | "SUCCESS" | "ERROR" | "FILE_SIZE_EXCEEDED" | "INVALID_TOKEN";

const TestResultImageUpload = (props: Props) => {

  const {getMessage, match} = props;

  const [cancelTokenSource/*, setCancelTokenSource*/] = React.useState(axios.CancelToken.source());
  const [pageState, setPageState] = React.useState("ENTRY" as PageState);
  const [officialCertificateWarning, setOfficialCertificateWarning] = React.useState(false);

  const [covidTestData, setCovidTestData] = useState({} as CovidTestRegistrationData);
  const [dataLoading, setDataLoading] = useState(true);
  const [testResult, setTestResult] = useState("");
  const [testResultSnapshot, setTestResultSnapshot] = useState(undefined);
  const [source, setSource] = useState("");
  const [ctValue, setCtValue] = useState(undefined as string | undefined);
  const [showCt, setShowCt] = useState(false);
  const [ctError, setCtError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        //@ts-ignore
        const result = await getCovidTestRegistrationData(match.params.token, cancelTokenSource)
        setCovidTestData(result);
        setDataLoading(false);
      } catch (e) {
        handleError(e.response.data);
      } finally {
        setDataLoading(false);
      }
    }

    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleError = (error: any) => {
    const knownErrors: Array<string> = [
      errorUtils.invalidToken,
      errorUtils.unknownEmployee
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode) && violation.errorCode === errorUtils.invalidToken) {
          setPageState("INVALID_TOKEN");
          setErrorMessage(getMessage(`error.${errorUtils.invalidToken}`));
        } else {
          setPageState("ERROR");
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      setPageState("ERROR");
      setErrorMessage(getMessage("error.generalErrorMessage"))
    }
  };

  const handleCapture = (target: any) => {
    if (target.files) {
      if (target.files.length !== 0) {
        const file = target.files[0];

        if (file.size <= 10485760) {
          setTestResultSnapshot(file);
          const newUrl = URL.createObjectURL(file);
          setSource(newUrl);
        } else {
          setPageState("FILE_SIZE_EXCEEDED");
        }
      }
    }
  };

  const setTestResultAndCtValue = (data: any) => {

    const isPcrTest = covidTestData.interventionExternalCode === InterventionExternalCode.PCR_SALIVA_TEST
        || covidTestData.interventionExternalCode === InterventionExternalCode.PCR_SALIVA_TEST_INDIVIDUAL_TEST;

    setTestResult(data.value as string);
    if (data.value === 'POSITIVE' && isPcrTest) {
      setShowCt(true);
    } else {
      setShowCt(false);
      setCtValue(undefined);
    }
  }

  const setTestCtValue = (data: any) => {

    if ("POSITIVE" === testResult) {
      const ctValue = data;
      setCtError(ctValidator(ctValue) !== undefined);
      setCtValue(data);
    } else {
      setCtValue(undefined);
    }
  }

  const parseCtInput = (value: any) => {
    if (!value || value.length < 6) {
      return value;
    } else {
      return value.slice(0, 5);
    }
  };

  const onUploadTestResult = async () => {
    const interventionExternalCode = covidTestData.interventionExternalCode;

    try {
      const responseDto = await registerCovidTestkitResult(
          //@ts-ignore
          match.params.token.toString(),
          testResult,
          testResultSnapshot,
          interventionExternalCode,
          ctValue,
          cancelTokenSource
      );

      if (responseDto.certificateError) {

        setOfficialCertificateWarning(true);
      }
      setPageState("SUCCESS");
    } catch (e) {
      if (e.message === "Network Error") {
        setPageState("FILE_SIZE_EXCEEDED");
      } else {
        handleError(e.response.data);
      }
    }
  };

  const takeSnapshotKey = source
      ? "covidTestkitSnapshot.action.retakeSnapshot"
      : "covidTestkitSnapshot.action.takeSnapshot";

  const renderTopPageData = () => <>
    <StyledInfoRow>
      <span>{getMessage("covidTestkitSnapshot.content.administrator")}</span>
      <span><b>{`${covidTestData.userName} ${covidTestData.userFirstName}`}</b></span>
    </StyledInfoRow>

    <h3>{getMessage("covidTestkitSnapshot.action.takeSnapshot")}</h3>

    <StyledInfoRow className="target-employee" backgroundColor="#e5e5e5">
      <span>{getMessage("covidTestkitSnapshot.content.employee")}</span>
      <span><b>{`${covidTestData.patientName} ${covidTestData.patientFirstName},
      ${moment(covidTestData.patientBirthDate).format("DD.MM.YYYY")}`}</b></span>
    </StyledInfoRow>
  </>;

  const renderEntryState = () => <>
    {renderTopPageData()}

    <Form className="result-form">
      <div className="snapshot-container">
        <input
            accept="image/*"
            className="image-input"
            id="icon-button-file"
            type="file"
            capture="environment"
            onChange={(e) => handleCapture(e.target)}
        />

        <label htmlFor="icon-button-file">
          {
            source
                ?
                <div className="image-box">
                  <img src={source} alt={"snap"} className=""/>
                </div>
                :
                <div className="snapshot-placeholder">
                  <Icon.Group size="huge">
                    <Icon name="flask"/>
                    <Icon corner="top right" name="address card outline"/>
                  </Icon.Group>
                </div>
          }
        </label>

        <label htmlFor="icon-button-file">
          <div className="take-snapshot-action">
            <Icon name="camera" size="large"/>
            <div className="action-label">
              {getMessage(takeSnapshotKey)}
            </div>
          </div>
        </label>
      </div>

      <div className="radio-box">
        <label htmlFor="testResult" className="field-label">
          {getMessage("covidTestkitSnapshot.form.testResult")}
        </label>
        <Form.Field>
          <Radio
              label={getMessage("enum.testResult.POSITIVE")}
              name="testResult"
              value="POSITIVE"
              checked={testResult === 'POSITIVE'}
              onChange={(e, data) => setTestResultAndCtValue(data)}
          />
        </Form.Field>
        <Form.Field>
          <Radio
              label={getMessage("enum.testResult.NEGATIVE")}
              name="testResult"
              value="NEGATIVE"
              checked={testResult === 'NEGATIVE'}
              onChange={(e, data) => setTestResultAndCtValue(data)}

          />
        </Form.Field>
        <Form.Field>
          <Radio
              label={getMessage("enum.testResult.UNCERTAIN")}
              name="testResult"
              value="UNCERTAIN"
              checked={testResult === 'UNCERTAIN'}
              onChange={(e, data) => setTestResultAndCtValue(data)}

          />
        </Form.Field>
      </div>

      {showCt &&
          <div className="ct-value">
            <label htmlFor="ctValue" className="field-label">
              {getMessage("patientCentral.testResult.ct")}:
            </label>
            <Form.Field error={ctError}>
              <Input
                  fluid
                  name="ctValue"
                  placeholder="##.##"
                  validate={ctValidator}
                  parse={parseCtInput}
                  className="input-field"
                  onChange={(e, data) => setTestCtValue(data.value)}
              />
            </Form.Field>
          </div>
      }

      <StyledButtonContainer>
        <Button
            type="button"
            className="action-button"
            primary
            compact
            onClick={onUploadTestResult}
            disabled={!testResult || !testResultSnapshot || (showCt && ctError)}
        >
          {getMessage('covidTestkitSnapshot.action.register')}
        </Button>
      </StyledButtonContainer>
    </Form>
  </>;

  const renderSuccess = () => <>
    <div className="success">
      {getMessage("covidTestkitSnapshot.message.success")}
    </div>
    {
      officialCertificateWarning &&
      <div className="warning">
        {getMessage("covidTestkitSnapshot.message.warn.officialCertificateNotGenerated")}
      </div>
    }
    {renderTopPageData()}
  </>;

  const renderError = () => <>
    <div className="error">
      {errorMessage}
    </div>
    {pageState !== "INVALID_TOKEN" && renderTopPageData()}

    <div className="info-message">
      {getMessage("covidTestkitSnapshot.message.somethingWentWrong")}
    </div>

    {pageState !== "INVALID_TOKEN" &&
    <StyledButtonContainer>
      <Button
          type="button"
          className="action-button"
          color="grey"
          onClick={() => setPageState("ENTRY")}
      >
        {getMessage('covidTestkitSnapshot.action.registerAgain')}
      </Button>
    </StyledButtonContainer>
    }
  </>;

  const renderFileSizeExceeded = () => <>
    <div className="error">
      {getMessage("covidTestkitSnapshot.message.fileSizeExceeded")}
    </div>
    {renderTopPageData()}

    <div className="info-message">
      {getMessage("covidTestkitSnapshot.message.smallerImage")}
    </div>

    <StyledButtonContainer>
      <Button
          type="button"
          className="action-button"
          color="grey"
          onClick={() => setPageState("ENTRY")}
      >
        {getMessage('covidTestkitSnapshot.action.registerAgain')}
      </Button>
    </StyledButtonContainer>
  </>;

  return <MainContainer>
    <div className="page-header">
      <div className="praxmedic-logo-container">
        <StyledLogo/>
      </div>
    </div>
    <StyledContainer>
      {dataLoading
          ? <StyledViewTableRow>
            <LoaderComponent active/>
          </StyledViewTableRow>
          : <>
            {
              pageState === "ENTRY" &&
              renderEntryState()
            }
            {
              pageState === "SUCCESS" &&
              renderSuccess()
            }
            {
              pageState === "ERROR" &&
              renderError()
            }
            {
              pageState === "INVALID_TOKEN" &&
                renderError()
            }
            {
              pageState === "FILE_SIZE_EXCEEDED" &&
              renderFileSizeExceeded()
            }
          </>
      }
    </StyledContainer>
    <div className="page-footer">
      <div className="left">{getMessage("page.footer.copyright")}</div>
      <div className="right">{getMessage("page.footer.appName")}</div>
    </div>
  </MainContainer>;
};

export default withRouterWorkaround(withMessages(TestResultImageUpload));
